
import React from "react";
import { Bell, Check, CheckCheck, Trash2, Calendar, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useNotifications, Notification } from "@/hooks/useNotifications";
import { formatDistanceToNow } from "date-fns";
import { ar, enUS } from "date-fns/locale";

interface NotificationsDropdownProps {
  isRTL: boolean;
}

export const NotificationsDropdown: React.FC<NotificationsDropdownProps> = ({ isRTL }) => {
  const { notifications, unreadCount, markAsRead, markAllAsRead, deleteNotification } = useNotifications();

  const formatTime = (dateString: string) => {
    return formatDistanceToNow(new Date(dateString), {
      addSuffix: true,
      locale: isRTL ? ar : enUS,
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative rounded-full"
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold"
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </motion.span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-80 md:w-96 p-0 rounded-2xl shadow-xl border-0"
        style={{
          background: 'linear-gradient(145deg, #ffffff, #f8f8f8)',
          boxShadow: '10px 10px 20px #d1d1d1, -10px -10px 20px #ffffff'
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-bold text-lg">
            {isRTL ? "الإشعارات" : "Notifications"}
          </h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={markAllAsRead}
              className="text-primary text-sm flex items-center gap-1"
            >
              <CheckCheck className="w-4 h-4" />
              {isRTL ? "قراءة الكل" : "Mark all read"}
            </Button>
          )}
        </div>

        {/* Notifications List */}
        <ScrollArea className="h-80">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <Bell className="w-12 h-12 mb-3 opacity-30" />
              <p className="text-sm">
                {isRTL ? "لا توجد إشعارات" : "No notifications"}
              </p>
            </div>
          ) : (
            <AnimatePresence>
              {notifications.map((notification, index) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  isRTL={isRTL}
                  formatTime={formatTime}
                  onMarkAsRead={() => markAsRead(notification.id)}
                  onDelete={() => deleteNotification(notification.id)}
                  index={index}
                />
              ))}
            </AnimatePresence>
          )}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

interface NotificationItemProps {
  notification: Notification;
  isRTL: boolean;
  formatTime: (date: string) => string;
  onMarkAsRead: () => void;
  onDelete: () => void;
  index: number;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  isRTL,
  formatTime,
  onMarkAsRead,
  onDelete,
  index,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.2, delay: index * 0.05 }}
      className={`p-4 border-b last:border-0 hover:bg-gray-50 transition-colors ${
        !notification.is_read ? "bg-blue-50/50" : ""
      }`}
    >
      <div className="flex gap-3">
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
            notification.type === "event" ? "bg-primary/10 text-primary" : "bg-gray-100 text-gray-600"
          }`}
        >
          <Calendar className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`font-semibold text-sm line-clamp-1 ${!notification.is_read ? "text-gray-900" : "text-gray-700"}`}>
            {isRTL ? notification.title_ar || notification.title : notification.title}
          </p>
          <p className="text-xs text-gray-500 line-clamp-2 mt-1">
            {isRTL ? notification.message_ar || notification.message : notification.message}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {formatTime(notification.created_at)}
          </p>
        </div>
        <div className="flex flex-col gap-1">
          {!notification.is_read && (
            <button
              onClick={onMarkAsRead}
              className="p-1 rounded-full hover:bg-green-100 text-green-600 transition-colors"
              title={isRTL ? "تحديد كمقروء" : "Mark as read"}
            >
              <Check className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={onDelete}
            className="p-1 rounded-full hover:bg-red-100 text-red-500 transition-colors"
            title={isRTL ? "حذف" : "Delete"}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};
